import type { ReactNode } from "react";
import { Camera, ImageIcon, ImagePlus } from "lucide-react";
import { Skeleton } from "@/components/ui/skeleton";
import { OfflineImage } from "@/components/common/offline-image/offline-image";
import { PhotoCountBadge } from "@/components/common/photo-count-badge/photo-count-badge";
import { ISSUE_CAMERA_QUERY } from "@/components/issues/issue-update-sheet/issue-camera-marker";
import { useMediaQuery } from "@/hooks/use-media-query";
import {
  firstUpdateIndex,
  type IssuePhoto,
} from "@/components/issues/issue-photo-viewer/issue-photos";

interface IssuePhotoSummaryProps {
  /** Every photo on the issue, oldest first; see `issuePhotos`. */
  photos: IssuePhoto[];
  /** The issue's record is still loading, so Before is not known yet. */
  recordLoading?: boolean;
  /** Opens the viewer on this photo. */
  onOpen: (photoId: string) => void;
  /**
   * Present when the viewer may post an update right now. An empty Latest
   * update column then becomes an Add progress photo button that calls it,
   * rather than a placeholder. The page passes it exactly when its bar offers
   * Update. See [[issue-photo-summary.spec]] § Posting from the empty tile.
   */
  onPostUpdate?: () => void;
}

/**
 * The issue's photos as two tiles side by side: the record's first photo under
 * **Before**, and the newest update photo under **Latest update**, each with
 * the Photos tab's count badge when it stands for more than one photo. Either
 * opens the whole issue in the viewer. See [[issue-photo-summary.spec]].
 */
export function IssuePhotoSummary({
  photos,
  recordLoading = false,
  onOpen,
  onPostUpdate,
}: IssuePhotoSummaryProps) {
  const split = firstUpdateIndex(photos);
  const before = photos.slice(0, split);
  const updates = photos.slice(split);

  // Nothing to show on either side, nothing on its way and nothing to post:
  // leave the block out rather than draw two empty frames.
  if (
    before.length === 0 &&
    updates.length === 0 &&
    !recordLoading &&
    !onPostUpdate
  ) {
    return null;
  }

  // The list runs oldest first, so the newest update photo is its last — an
  // update with no photo adds nothing and leaves this tile on the previous one.
  const latest = updates.at(-1);

  return (
    <div className="mt-3 grid max-w-[560px] grid-cols-2 gap-3">
      <SummaryColumn title="Before">
        {before.length > 0 ? (
          <SummaryTile
            photo={before[0]}
            count={before.length}
            label={
              before.length > 1
                ? `Open before photos, ${before.length} photos`
                : "Open before photo"
            }
            onOpen={onOpen}
          />
        ) : recordLoading ? (
          <Skeleton
            data-testid="issue-photo-summary-loading"
            className="aspect-square w-full rounded-md"
          />
        ) : (
          <SummaryPlaceholder text="No photos" />
        )}
      </SummaryColumn>

      <SummaryColumn title="Latest update">
        {latest ? (
          <SummaryTile
            photo={latest}
            count={updates.length}
            label={
              updates.length > 1
                ? `Open latest update photo, ${updates.length} update photos`
                : "Open latest update photo"
            }
            onOpen={onOpen}
          />
        ) : onPostUpdate ? (
          <PostUpdateTile onClick={onPostUpdate} />
        ) : (
          <SummaryPlaceholder text="No update photos yet" />
        )}
      </SummaryColumn>
    </div>
  );
}

function SummaryColumn({
  title,
  children,
}: {
  title: string;
  children: ReactNode;
}) {
  return (
    <div className="min-w-0">
      <p className="mb-1.5 text-xs text-muted-foreground">{title}</p>
      {children}
    </div>
  );
}

function SummaryTile({
  photo,
  count,
  label,
  onOpen,
}: {
  photo: IssuePhoto;
  count: number;
  label: string;
  onOpen: (photoId: string) => void;
}) {
  return (
    <button
      type="button"
      aria-label={label}
      onClick={() => onOpen(photo.id)}
      className="relative block aspect-square w-full overflow-hidden rounded-md bg-muted focus:outline-none focus-visible:ring-2 focus-visible:ring-ring"
    >
      {/* Through OfflineImage for its anonymous CORS, and from the same
          thumbnail address the viewer's rows load: a plain no-CORS <img> of
          that S3 object poisons the cache entry the viewer then reads, and its
          thumbnails show broken. No offline key, since issue photos are not in
          the asset cache. See [[s3-image-fetching]]. */}
      <OfflineImage
        assetKey=""
        src={photo.thumbnailSrc}
        alt=""
        loading="lazy"
        className="h-full w-full object-cover"
      />
      <PhotoCountBadge count={count} />
    </button>
  );
}

/**
 * The empty Latest update column for someone who may post: the way to post the
 * fix photo. The placeholder's dashed square, with the Update button's mint
 * kept to a ghost disc around the icon that fills solid on hover or press. A
 * camera on a device the update sheet opens its camera on, a photo-with-plus
 * elsewhere, so the icon never promises a camera that will not open.
 */
function PostUpdateTile({ onClick }: { onClick: () => void }) {
  const camera = useMediaQuery(ISSUE_CAMERA_QUERY);
  const Icon = camera ? Camera : ImagePlus;
  return (
    <button
      type="button"
      onClick={onClick}
      className="group flex aspect-square w-full flex-col items-center justify-center gap-2.5 rounded-md border border-dashed border-foreground/15 bg-muted/40 p-3 text-center transition-colors hover:bg-muted/70 active:bg-muted/70 focus:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background"
    >
      {/* A ring ripples out from the disc while the tile waits; it stops on
          hover and under reduced motion. See [[issue-photo-summary.spec]]. */}
      <span
        data-slot="progress-photo-disc"
        className="progress-photo-ripple relative grid size-14 place-items-center rounded-full bg-primary/20 text-primary-ink ring-1 ring-inset ring-primary/40 transition-colors group-hover:bg-primary group-hover:text-primary-foreground group-active:bg-primary group-active:text-primary-foreground"
      >
        <Icon className="h-6 w-6" aria-hidden="true" />
      </span>
      <span className="text-sm font-semibold">Add progress photo</span>
    </button>
  );
}

function SummaryPlaceholder({ text }: { text: string }) {
  return (
    <div className="flex aspect-square w-full flex-col items-center justify-center gap-2 rounded-md border border-dashed border-border bg-muted/40 p-3 text-center">
      <ImageIcon className="h-5 w-5 text-muted-foreground" aria-hidden="true" />
      <p className="text-xs font-medium text-muted-foreground">{text}</p>
    </div>
  );
}
